<template>
  <tr class="ss-dashboard-staff-list-row">
    <td>
      <span class="ss-dashboard-staff-list-row__name">
        <span
          v-if="this.rank"
          class="ss-dashboard-staff-list-row__rank"
        >
          {{ this.rank }}
        </span>
        {{ this.item.name }}
      </span>
    </td>
    <td>{{ $_ssDSLR_roleLabel(this.item.role) }}</td>
    <td>{{ this.item.performance.sentStyleCount }}</td>
    <td>{{ this.item.performance.viewedStyleCount }}</td>
    <td>{{ this.item.performance.favoritedStyleCount }}</td>
    <td>{{ this.item.performance.addedToCartCount }}</td>
    <td>{{ this.item.performance.purchasedCount }}</td>
    <td>{{ this.item.performance.purchasedCustomerCount }}</td>
    <td>{{ $_ssDSLR_formatSalesAmount(this.item.performance.salesAmount) }}</td>
  </tr>
</template>

<script>
/**
 * Performance metrics for a single staff member over the selected period.
 * @typedef {Object} StaffPerformance
 * @property {number} sentStyleCount - Number of styles sent by the staff.
 * @property {number} viewedStyleCount - Number of sent styles that were viewed by customers.
 * @property {number} favoritedStyleCount - Number of sent styles that were favorited by customers.
 * @property {number} addedToCartCount - Number of styles added to a cart.
 * @property {number} purchasedCount - Number of styles that were purchased.
 * @property {number} purchasedCustomerCount - Number of distinct customers who purchased.
 * @property {number} salesAmount - Total sales amount in JPY (integer yen).
 */

/**
 * Staff entry rendered on a single row of the dashboard staff list.
 * @typedef {Object} StaffItem
 * @property {string} name - Staff member's display name.
 * @property {string} role - Staff member's role label (e.g. "スタイリスト").
 * @property {StaffPerformance} performance - Aggregated performance metrics for the row.
 */

export default {
  name: 'SSDashboardStaffListRow',
  components: {
  },
  props: {
    /**
     * Staff entry displayed on this row.
     * @type {StaffItem}
     */
    item: {
      type: Object,
      required: true,
      default: () => {},
    },
    /**
     * Ranking number shown as a badge next to the staff name.
     * Pass `null` to hide the badge.
     * @type {number | null}
     */
    rank: {
      type: [Number, null],
      default: null,
    },
  },
  methods: {
    /**
     * Format a sales amount as a Japanese yen currency string.
     * @param {number} amount - Sales amount in JPY.
     * @returns {string} Localized currency string (e.g. "￥344,740").
     */
    $_ssDSLR_formatSalesAmount(amount) {
      return amount.toLocaleString('ja-JP', { style: 'currency', currency: 'JPY' });
    },
    /**
     * Get the label for a staff role.
     * @param {number} role - Staff role ID.
     * @returns {string} Staff role label.
     */
    $_ssDSLR_roleLabel(role) {
      return {
        1: 'オーナー',
        2: 'スタイリスト',
        3: 'アシスタント',
        4: 'スタッフ',
      }[role];
    },
  },
}
</script>

<style scoped lang="scss">
@import "../theme/variables";
@import "../grid/variables";
@import "../typography/variables";
@import "../typography/mixins";

.ss-dashboard-staff-list-row {
  td {
    font-size: oct-rem(14);
    line-height: 1;
    text-align: center;
    height: oct-rem(48);
    padding: 0 oct-rem(16);
    vertical-align: middle;
    min-width: oct-rem(72);
    border-top: oct-rem(1) solid $oct-theme--neutral-20;

    &:first-child {
      padding-left: oct-rem(32);
    }

    &:last-child {
      padding-right: oct-rem(32);
    }
  }
}

.ss-dashboard-staff-list-row__name {
  display: flex;
  align-items: center;
  gap: oct-rem(8);
}

.ss-dashboard-staff-list-row__rank {
  font-size: oct-rem(12);
  line-height: 1;
  color: $oct-theme--on-primary;
  display: flex;
  justify-content: center;
  align-items: center;
  width: oct-rem(18);
  height: oct-rem(24);
  border-radius: oct-rem(9);
  background-color: $oct-theme--neutral;
}

</style>